<template>
  <div class="container mx-auto px-4 py-4 text-gray-900 dark:text-gray-100">
    <div class="flex flex-col gap-6">
      <PageHeading
        title="Songs"
        :description="`Total: ${songs.length} songs`"
        :crumbs="[{ label: 'Songs' }, { label: 'All Songs' }]"
      >
        <template #actions>
          <PagePrimaryButton @click="openAddModal">
            <PlusIcon class="h-4 w-4 shrink-0" />
            Add New Song
          </PagePrimaryButton>
        </template>
      </PageHeading>

      <div class="flex flex-col gap-3 lg:flex-row lg:items-center">
        <div class="relative flex-1">
          <MagnifyingGlassIcon
            class="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
          />
          <input
            v-model="songSearch"
            type="search"
            placeholder="Search by title, artist, genre, year, or category..."
            aria-label="Search songs"
            class="h-9 w-full rounded-md border border-gray-300 bg-transparent py-1 pl-9 pr-3 text-sm text-gray-900 shadow-sm outline-none transition-all placeholder:text-gray-400 focus:border-primary-500 focus:ring-[3px] focus:ring-primary-500/20 dark:border-gray-600 dark:text-white"
          />
        </div>
        <div class="flex flex-wrap items-center gap-2">
          <div class="relative">
            <select
              v-model="statusFilter"
              aria-label="Status"
              :class="[songFilterClass, 'w-[140px]']"
            >
              <option value="all">All Status</option>
              <option value="active">Active</option>
              <option value="disabled">Disabled</option>
            </select>
            <ChevronDownIcon
              class="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
            />
          </div>
          <div class="relative">
            <select
              v-model="genreFilter"
              aria-label="Genre"
              :class="[songFilterClass, 'w-[150px]']"
            >
              <option value="all">All Genres</option>
              <option v-for="genre in genres" :key="genre.id || genre.name" :value="genre.name">
                {{ genre.name }}
              </option>
            </select>
            <ChevronDownIcon
              class="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
            />
          </div>
          <div class="relative">
            <select
              v-model="categoryFilter"
              aria-label="Category"
              :class="[songFilterClass, 'w-[160px]']"
            >
              <option value="all">All Categories</option>
              <option
                v-for="category in categories"
                :key="category.id || category.name"
                :value="category.name"
              >
                {{ category.name }}
              </option>
            </select>
            <ChevronDownIcon
              class="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
            />
          </div>
        </div>
      </div>

      <div
        class="rounded-xl border border-gray-200 bg-white py-6 shadow-sm dark:border-gray-700 dark:bg-gray-900"
      >
        <div class="overflow-x-auto px-6">
          <div v-if="loading" class="py-8 text-center">
            <div
              class="mx-auto h-8 w-8 animate-spin rounded-full border-b-2 border-primary-600"
            ></div>
          </div>

          <div v-else-if="filteredSongs.length === 0" class="py-8 text-center">
            <p class="text-gray-500 dark:text-gray-400">
              {{
                songs.length === 0
                  ? "No songs found. Add your first song to get started!"
                  : "No songs match these filters."
              }}
            </p>
          </div>

          <DataTable
            v-else
            :value="filteredSongs"
            paginator
            :rows="rows"
            :rows-per-page-options="[10, 25, 50, 100]"
            class="p-datatable-sm"
          >
            <Column field="title" header="Title" sortable>
              <template #body="{ data }">
                <span class="font-medium text-gray-900 dark:text-white">{{ data.title }}</span>
              </template>
            </Column>

            <Column field="author" header="Artist" sortable>
              <template #body="{ data }">
                <span class="whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{{
                  data.author
                }}</span>
              </template>
            </Column>

            <Column field="genre" header="Genre" sortable>
              <template #body="{ data }">
                <span class="whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">{{
                  data.genre || "—"
                }}</span>
              </template>
            </Column>

            <Column field="category" header="Categories" sortable>
              <template #body="{ data }">
                <span class="text-sm text-gray-500 dark:text-gray-400">
                  {{ songCategoriesLabel(data) }}
                </span>
              </template>
            </Column>

            <Column field="isDisabled" header="Status" sortable>
              <template #body="{ data }">
                <ToggleSwitch
                  :model-value="!data.isDisabled"
                  :disabled="togglingSongId === data.id"
                  :aria-label="data.isDisabled ? 'Enable song' : 'Disable song'"
                  :class="data.isDisabled ? 'song-toggle-disabled' : ''"
                  @update:model-value="(value) => toggleSongStatus(data, !value)"
                />
              </template>
            </Column>

            <Column header="" :exportable="false" style="width: 96px">
              <template #body="{ data }">
                <div class="flex items-center justify-end gap-1">
                  <button
                    class="inline-flex h-8 w-8 items-center justify-center rounded-md text-gray-500 transition-colors hover:bg-gray-100 hover:text-gray-900 dark:text-gray-400 dark:hover:bg-gray-800 dark:hover:text-white"
                    title="Edit song"
                    @click="openEditModal(data)"
                  >
                    <PencilIcon class="h-4 w-4" />
                  </button>
                  <button
                    :disabled="deletingSongId === data.id"
                    class="inline-flex h-8 w-8 items-center justify-center rounded-md text-red-600 transition-colors hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50 dark:hover:bg-red-950/40"
                    :title="deletingSongId === data.id ? 'Deleting...' : 'Delete song'"
                    @click="handleDeleteSong(data)"
                  >
                    <TrashIcon class="h-4 w-4" />
                  </button>
                </div>
              </template>
            </Column>
          </DataTable>
        </div>
      </div>
    </div>

    <!-- Add/Edit Song Modal -->
    <div
      v-if="showSongModal"
      class="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4"
      @click.self="handleOutsideClick"
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="song-modal-title"
        aria-describedby="song-modal-description"
        class="relative flex max-h-[90vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg bg-white shadow-2xl dark:bg-gray-900"
      >
        <button
          type="button"
          class="absolute right-4 top-4 z-10 rounded-sm text-gray-500 opacity-70 transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:text-gray-400"
          aria-label="Close"
          @click="handleOutsideClick"
        >
          <XMarkIcon class="h-4 w-4" />
        </button>

        <div
          class="shrink-0 border-b border-primary-500/10 bg-primary-500/5 p-6 dark:bg-primary-400/10"
        >
          <div class="mb-2 flex items-center gap-3 pr-8">
            <div
              class="flex h-10 w-10 items-center justify-center rounded-xl bg-primary-500/10 text-primary-600 dark:text-primary-300"
            >
              <MusicalNoteIcon class="h-5 w-5" />
            </div>
            <div>
              <h2
                id="song-modal-title"
                class="text-xl font-bold tracking-tight text-gray-900 dark:text-white"
              >
                {{ editingSong ? "Edit Song" : "Add New Song" }}
              </h2>
              <p id="song-modal-description" class="text-xs text-gray-500 dark:text-gray-400">
                {{ editingSong ? "Update song information" : "Add a new song to the library" }}
              </p>
            </div>
          </div>
        </div>

        <form
          class="flex min-h-0 flex-1 flex-col bg-white dark:bg-gray-900"
          @submit.prevent="handleSaveSong"
        >
          <div class="min-h-0 flex-1 space-y-6 overflow-y-auto p-6">
            <div class="grid grid-cols-1 gap-6 md:grid-cols-2">
              <div class="space-y-2 md:col-span-2">
                <label for="song-title" :class="songLabelClass">Title</label>
                <div class="relative">
                  <MusicalNoteIcon
                    class="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400/70"
                  />
                  <input
                    id="song-title"
                    v-model="songForm.title"
                    type="text"
                    required
                    placeholder="Enter song title"
                    :class="[songFieldClass, 'pl-9']"
                  />
                </div>
              </div>

              <div class="space-y-2 md:col-span-2">
                <label for="song-author" :class="songLabelClass">Artist</label>
                <div class="relative">
                  <UserIcon
                    class="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400/70"
                  />
                  <input
                    id="song-author"
                    v-model="songForm.author"
                    type="text"
                    required
                    placeholder="Enter artist"
                    :class="[songFieldClass, 'pl-9']"
                  />
                </div>
              </div>

              <div
                class="space-y-2"
                :class="showCategoryMenu ? 'relative z-30' : ''"
                @focusout="closeCategoryMenu"
              >
                <span :class="songLabelClass">Categories</span>
                <div class="relative">
                  <TagIcon
                    class="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400/70"
                  />
                  <button
                    id="song-category"
                    type="button"
                    :class="[songFieldClass, 'flex items-center pl-9 pr-9 text-left']"
                    :aria-expanded="showCategoryMenu"
                    aria-haspopup="listbox"
                    @click="showCategoryMenu = !showCategoryMenu"
                  >
                    <span
                      class="truncate"
                      :class="songForm.categories.length ? '' : 'text-gray-400'"
                    >
                      {{
                        songForm.categories.length
                          ? songForm.categories.join(", ")
                          : "Select categories"
                      }}
                    </span>
                  </button>
                  <ChevronDownIcon
                    class="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                  />
                  <div
                    v-if="showCategoryMenu"
                    class="absolute left-0 right-0 top-full z-20 mt-1 max-h-48 overflow-auto rounded-md border border-gray-200 bg-white py-1 shadow-lg dark:border-gray-700 dark:bg-gray-900"
                    role="listbox"
                    aria-multiselectable="true"
                  >
                    <p
                      v-if="categoryChoices.length === 0"
                      class="px-3 py-2 text-sm text-gray-500 dark:text-gray-400"
                    >
                      No categories yet
                    </p>
                    <label
                      v-for="name in categoryChoices"
                      :key="name"
                      class="flex cursor-pointer items-center gap-2 px-3 py-2 text-sm text-gray-900 hover:bg-gray-50 dark:text-white dark:hover:bg-gray-800"
                    >
                      <input
                        v-model="songForm.categories"
                        type="checkbox"
                        :value="name"
                        class="h-4 w-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      {{ name }}
                    </label>
                  </div>
                </div>
              </div>

              <div class="space-y-2">
                <label for="song-genre" :class="songLabelClass">Genre</label>
                <div class="relative">
                  <select
                    id="song-genre"
                    v-model="songForm.genre"
                    :class="[songFieldClass, 'appearance-none pr-9']"
                  >
                    <option value="">Select a genre</option>
                    <option v-for="name in genreChoices" :key="name" :value="name">
                      {{ name }}
                    </option>
                  </select>
                  <ChevronDownIcon
                    class="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400"
                  />
                </div>
              </div>

              <div
                class="space-y-3 border-t border-gray-200 pt-6 md:col-span-2 dark:border-gray-700"
              >
                <span :class="songLabelClass">Song Length</span>
                <div class="flex items-center gap-2">
                  <input
                    id="song-length-min"
                    :value="songForm.songLengthMin ?? ''"
                    type="number"
                    min="0"
                    max="999"
                    placeholder="0"
                    aria-label="Minutes"
                    :class="[songFieldClass, '!w-20 shrink-0 px-2 text-center']"
                    @input="setSongLengthPart('songLengthMin', $event, 999)"
                  />
                  <span class="text-lg font-semibold text-gray-500 dark:text-gray-400">:</span>
                  <input
                    id="song-length-sec"
                    :value="songForm.songLengthSec ?? ''"
                    type="number"
                    min="0"
                    max="59"
                    placeholder="00"
                    aria-label="Seconds"
                    :class="[songFieldClass, '!w-20 shrink-0 px-2 text-center']"
                    @input="setSongLengthPart('songLengthSec', $event, 59)"
                  />
                  <span class="whitespace-nowrap text-sm text-gray-400 dark:text-gray-500"
                    >mm : ss</span
                  >
                </div>
                <div
                  class="flex items-center gap-3 rounded-lg border border-primary-500/10 bg-primary-500/5 p-3 text-xs text-gray-500 dark:text-gray-400"
                >
                  <InformationCircleIcon
                    class="h-4 w-4 shrink-0 text-primary-600 dark:text-primary-300"
                  />
                  <p>Song length is used by the setlist feature to calculate set duration.</p>
                </div>
              </div>

              <div class="border-t border-gray-200 pt-2 md:col-span-2 dark:border-gray-700">
                <p :class="songLabelClass">Own use only</p>
              </div>

              <div class="space-y-2">
                <label for="song-language" :class="songLabelClass">Language</label>
                <div class="relative">
                  <GlobeAltIcon
                    class="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400/70"
                  />
                  <input
                    id="song-language"
                    v-model="songForm.language"
                    type="text"
                    placeholder="Enter language"
                    :class="[songFieldClass, 'pl-9']"
                  />
                </div>
              </div>
            </div>

            <div class="space-y-2">
              <label for="song-notes" :class="songLabelClass">Notes</label>
              <textarea
                id="song-notes"
                v-model="songForm.notes"
                rows="3"
                placeholder="Enter optional notes about the song"
                :class="[songFieldClass, 'h-auto py-2']"
              />
            </div>

            <div
              class="flex items-center justify-between rounded-xl border border-gray-200 bg-gray-50 p-4 dark:border-gray-700 dark:bg-gray-800/40"
            >
              <div class="space-y-0.5">
                <p class="text-sm font-bold tracking-tight text-gray-900 dark:text-white">
                  Song status
                </p>
                <p class="text-[10px] leading-tight text-gray-500 dark:text-gray-400">
                  {{
                    songForm.isDisabled
                      ? "Song is disabled and will not be visible for voting"
                      : "Song is active and available for voting"
                  }}
                </p>
              </div>
              <button
                type="button"
                role="switch"
                :aria-checked="String(!songForm.isDisabled)"
                aria-label="Song status"
                :class="[
                  'relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2',
                  songForm.isDisabled ? 'bg-gray-200 dark:bg-gray-600' : 'bg-primary-600'
                ]"
                @click="songForm.isDisabled = !songForm.isDisabled"
              >
                <span
                  :class="[
                    'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out',
                    songForm.isDisabled ? 'translate-x-0' : 'translate-x-5'
                  ]"
                />
              </button>
            </div>

            <div
              v-if="songError"
              class="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-600 dark:border-red-700 dark:bg-red-900/40 dark:text-red-300"
            >
              {{ songError }}
            </div>
          </div>

          <div
            class="flex shrink-0 justify-end gap-3 border-t border-gray-200 px-6 py-4 dark:border-gray-700"
          >
            <button
              type="button"
              class="inline-flex h-9 items-center justify-center rounded-md px-4 text-sm font-medium text-gray-700 transition-all hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-800"
              @click="cancelSongModal"
            >
              Cancel
            </button>
            <button
              type="submit"
              :disabled="songLoading"
              class="inline-flex h-9 items-center justify-center gap-2 rounded-md bg-primary-600 px-8 text-sm font-medium text-white shadow-lg shadow-primary-500/20 transition-all hover:scale-[1.02] hover:bg-primary-700 disabled:pointer-events-none disabled:opacity-50 disabled:hover:scale-100"
            >
              <MusicalNoteIcon class="h-4 w-4" />
              {{ songLoading ? "Saving..." : editingSong ? "Save Changes" : "Add Song" }}
            </button>
          </div>
        </form>
      </div>
    </div>

    <!-- Delete Confirmation Modal -->
    <div
      v-if="showDeleteModal"
      class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50"
      @click.self="cancelDelete"
    >
      <div
        class="bg-white dark:bg-gray-900 rounded-lg border border-gray-200 dark:border-gray-700 max-w-md w-full mx-4"
      >
        <div class="p-6">
          <div class="flex items-center mb-4">
            <div
              class="flex-shrink-0 mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-red-100 dark:bg-red-900/20"
            >
              <svg
                class="h-6 w-6 text-red-600 dark:text-red-400"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                />
              </svg>
            </div>
          </div>
          <h3 class="text-lg font-medium text-gray-900 dark:text-white text-center mb-2">
            Delete Song
          </h3>
          <p class="text-sm text-gray-500 dark:text-gray-400 text-center mb-6">
            Are you sure you want to delete <strong>{{ songToDelete?.title }}</strong> by
            <strong>{{ songToDelete?.author }}</strong
            >? This action cannot be undone.
          </p>
          <div class="flex space-x-3">
            <button class="btn btn-secondary flex-1" @click="cancelDelete">Cancel</button>
            <button
              :disabled="deletingSongId !== null"
              class="btn btn-danger flex-1 disabled:opacity-50 disabled:cursor-not-allowed"
              @click="confirmDelete"
            >
              {{ deletingSongId !== null ? "Deleting..." : "Delete" }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, computed, onMounted } from "vue";
import { useToast } from "primevue/usetoast";
import { useConfirm } from "primevue/useconfirm";
import DataTable from "primevue/datatable";
import Column from "primevue/column";
import ToggleSwitch from "primevue/toggleswitch";
import {
  ChevronDownIcon,
  GlobeAltIcon,
  InformationCircleIcon,
  MagnifyingGlassIcon,
  MusicalNoteIcon,
  PencilIcon,
  PlusIcon,
  TagIcon,
  TrashIcon,
  UserIcon,
  XMarkIcon
} from "@heroicons/vue/24/outline";
import api from "../../services/api";
import PageHeading from "../../components/common/PageHeading.vue";
import PagePrimaryButton from "../../components/common/PagePrimaryButton.vue";

export default {
  name: "AdminSongs",
  components: {
    DataTable,
    Column,
    ToggleSwitch,
    ChevronDownIcon,
    GlobeAltIcon,
    InformationCircleIcon,
    MagnifyingGlassIcon,
    MusicalNoteIcon,
    PencilIcon,
    PlusIcon,
    TagIcon,
    TrashIcon,
    UserIcon,
    XMarkIcon,
    PageHeading,
    PagePrimaryButton
  },
  setup() {
    const toast = useToast();
    const confirm = useConfirm();
    const songs = ref([]);
    const loading = ref(true);
    const categories = ref([]);
    const genres = ref([]);
    const showSongModal = ref(false);
    const rows = ref(10);
    const songSearch = ref("");
    const statusFilter = ref("all");
    const genreFilter = ref("all");
    const categoryFilter = ref("all");
    const songFilterClass =
      "h-9 appearance-none rounded-md border border-gray-300 bg-transparent py-2 pl-3 pr-8 text-sm text-gray-900 shadow-sm outline-none focus:border-primary-500 focus:ring-[3px] focus:ring-primary-500/20 dark:border-gray-600 dark:text-white";
    const songLabelClass =
      "text-xs font-bold uppercase tracking-wider text-gray-500 dark:text-gray-400";
    const songFieldClass =
      "h-11 w-full rounded-md border border-gray-300 bg-transparent px-3 text-sm text-gray-900 shadow-sm outline-none transition-all placeholder:text-gray-400 focus:border-primary-500 focus:ring-[3px] focus:ring-primary-500/20 dark:border-gray-600 dark:bg-gray-900 dark:text-white";
    const showCategoryMenu = ref(false);
    const editingSong = ref(null);
    const songForm = ref({
      title: "",
      author: "",
      genre: "",
      songLengthMin: null,
      songLengthSec: null,
      categories: [],
      language: "",
      notes: "",
      isDisabled: false
    });

    // Convert seconds (integer) → { min, sec } for the two InputNumber fields
    const secondsToMinSec = (totalSeconds) => {
      if (totalSeconds === null || totalSeconds === undefined) return { min: null, sec: null };
      return { min: Math.floor(totalSeconds / 60), sec: totalSeconds % 60 };
    };

    // Convert { min, sec } → seconds (integer), returns null if both are empty
    const minSecToSeconds = (min, sec) => {
      if ((min === null || min === undefined) && (sec === null || sec === undefined)) return null;
      return (min || 0) * 60 + (sec || 0);
    };
    const songError = ref("");
    const songLoading = ref(false);
    const togglingSongId = ref(null);
    const showDeleteModal = ref(false);
    const songToDelete = ref(null);
    const deletingSongId = ref(null);

    const fetchSongs = async () => {
      try {
        loading.value = true;
        const response = await api.get("/api/songs");
        if (response.data.success) {
          songs.value = response.data.data.songs || [];
        }
      } catch (error) {
        console.error("Failed to fetch songs:", error);
      } finally {
        loading.value = false;
      }
    };

    const songCategoryNames = (song) => {
      if (Array.isArray(song.categories) && song.categories.length > 0) return song.categories;
      if (song.category) return [song.category];
      return [];
    };

    const songCategoriesLabel = (song) => {
      const names = songCategoryNames(song);
      return names.length > 0 ? names.join(", ") : "—";
    };

    const categoryChoices = computed(() => {
      const names = categories.value.map((category) => category.name).filter(Boolean);
      const extras = (songForm.value.categories || []).filter(
        (name) => name && !names.includes(name)
      );
      return [...names, ...extras];
    });

    const genreChoices = computed(() => {
      const names = genres.value.map((genre) => genre.name).filter(Boolean);
      if (songForm.value.genre && !names.includes(songForm.value.genre)) {
        return [songForm.value.genre, ...names];
      }
      return names;
    });

    const setSongLengthPart = (key, event, max) => {
      const raw = event.target.value;
      if (raw === "") {
        songForm.value[key] = null;
        return;
      }
      const value = Math.min(max, Math.max(0, Number(raw)));
      songForm.value[key] = Number.isNaN(value) ? null : value;
    };

    const closeCategoryMenu = (event) => {
      if (event?.relatedTarget && event.currentTarget?.contains(event.relatedTarget)) return;
      showCategoryMenu.value = false;
    };

    const filteredSongs = computed(() => {
      const query = songSearch.value.trim().toLowerCase();
      return songs.value.filter((song) => {
        if (statusFilter.value === "active" && song.isDisabled) return false;
        if (statusFilter.value === "disabled" && !song.isDisabled) return false;
        if (genreFilter.value !== "all" && song.genre !== genreFilter.value) return false;
        if (
          categoryFilter.value !== "all" &&
          !songCategoryNames(song).includes(categoryFilter.value)
        ) {
          return false;
        }
        if (!query) return true;
        const haystack = [
          song.title,
          song.author,
          song.genre,
          song.year,
          song.language,
          song.category,
          ...songCategoryNames(song)
        ]
          .filter((value) => value !== null && value !== undefined && value !== "")
          .join(" ")
          .toLowerCase();
        return haystack.includes(query);
      });
    });

    const fetchCategories = async () => {
      try {
        const response = await api.get("/api/categories");
        if (response.data.success) {
          categories.value = response.data.data.categories || [];
        }
      } catch (error) {
        console.error("Failed to fetch categories:", error);
      }
    };

    const fetchGenres = async () => {
      try {
        const response = await api.get("/api/genres");
        if (response.data.success) {
          genres.value = response.data.data.genres || [];
        }
      } catch (error) {
        console.error("Failed to fetch genres:", error);
      }
    };

    const openAddModal = () => {
      editingSong.value = null;
      songForm.value = {
        title: "",
        author: "",
        genre: "",
        songLengthMin: null,
        songLengthSec: null,
        categories: [],
        language: "",
        notes: "",
        isDisabled: false
      };
      songError.value = "";
      showCategoryMenu.value = false;
      showSongModal.value = true;
    };

    const openEditModal = (song) => {
      editingSong.value = song;
      // Handle both old format (single category string) and new format (categories array)
      let categories = [];
      if (song.categories && Array.isArray(song.categories)) {
        categories = song.categories;
      } else if (song.category) {
        // Backward compatibility: if old single category exists, convert to array
        categories = [song.category];
      }
      const { min, sec } = secondsToMinSec(song.songLength);
      songForm.value = {
        title: song.title || "",
        author: song.author || "",
        genre: song.genre || "",
        songLengthMin: min,
        songLengthSec: sec,
        categories: categories,
        language: song.language || "",
        notes: song.notes || "",
        isDisabled: song.isDisabled || false
      };
      songError.value = "";
      showCategoryMenu.value = false;
      showSongModal.value = true;
    };

    const cancelSongModal = () => {
      showSongModal.value = false;
      editingSong.value = null;
      songForm.value = {
        title: "",
        author: "",
        genre: "",
        songLengthMin: null,
        songLengthSec: null,
        categories: [],
        language: "",
        notes: "",
        isDisabled: false
      };
      songError.value = "";
      showCategoryMenu.value = false;
    };

    const handleOutsideClick = () => {
      // Only show confirmation when adding a new song (not editing)
      if (!editingSong.value) {
        // Check if form has any data entered
        const hasFormData =
          songForm.value.title.trim() ||
          songForm.value.author.trim() ||
          songForm.value.genre.trim() ||
          songForm.value.songLengthMin !== null ||
          songForm.value.songLengthSec !== null ||
          (songForm.value.categories && songForm.value.categories.length > 0) ||
          songForm.value.language.trim() ||
          songForm.value.notes.trim();

        if (hasFormData) {
          confirm.require({
            message:
              "Are you sure you want to cancel adding this song? Any unsaved changes will be lost.",
            header: "Cancel Adding Song",
            icon: "pi pi-exclamation-triangle",
            rejectProps: {
              label: "No, Continue Editing",
              severity: "secondary",
              outlined: true
            },
            acceptProps: {
              label: "Yes, Cancel",
              severity: "danger"
            },
            accept: () => {
              cancelSongModal();
            },
            reject: () => {
              // User chose to continue editing, do nothing
            }
          });
        } else {
          // No data entered, close without confirmation
          cancelSongModal();
        }
      } else {
        // Editing mode - close without confirmation (existing behavior)
        cancelSongModal();
      }
    };

    const toggleSongStatus = async (song, newValue) => {
      try {
        togglingSongId.value = song.id;

        const response = await api.put(`/api/songs/${song.id}`, {
          isDisabled: newValue
        });

        if (response.data.success) {
          // Update the song in the local list
          const index = songs.value.findIndex((s) => s.id === song.id);
          if (index !== -1) {
            songs.value[index].isDisabled = newValue;
          }
        } else {
          console.error("Failed to toggle song status:", response.data.message);
          // Revert the change on error
          const index = songs.value.findIndex((s) => s.id === song.id);
          if (index !== -1) {
            songs.value[index].isDisabled = !newValue;
          }
        }
      } catch (err) {
        console.error("Toggle song status error:", err);
        // Revert the change on error
        const index = songs.value.findIndex((s) => s.id === song.id);
        if (index !== -1) {
          songs.value[index].isDisabled = !newValue;
        }
      } finally {
        togglingSongId.value = null;
      }
    };

    const handleDeleteSong = (song) => {
      songToDelete.value = song;
      showDeleteModal.value = true;
    };

    const cancelDelete = () => {
      showDeleteModal.value = false;
      songToDelete.value = null;
    };

    const confirmDelete = async () => {
      if (!songToDelete.value) return;

      try {
        deletingSongId.value = songToDelete.value.id;
        const response = await api.delete(`/api/songs/${songToDelete.value.id}`);

        if (response.data.success) {
          // Remove song from local list
          songs.value = songs.value.filter((s) => s.id !== songToDelete.value.id);
          showDeleteModal.value = false;
          songToDelete.value = null;
        } else {
          console.error("Failed to delete song:", response.data.message);
        }
      } catch (err) {
        console.error("Delete song error:", err);
      } finally {
        deletingSongId.value = null;
      }
    };

    const handleSaveSong = async () => {
      try {
        songLoading.value = true;
        songError.value = "";

        // Prepare data - convert empty strings to null for optional fields
        const songData = {
          title: songForm.value.title.trim(),
          author: songForm.value.author.trim(),
          genre: songForm.value.genre.trim() || null,
          songLength: minSecToSeconds(songForm.value.songLengthMin, songForm.value.songLengthSec),
          categories:
            songForm.value.categories && songForm.value.categories.length > 0
              ? songForm.value.categories
              : null,
          language: songForm.value.language.trim() || null,
          notes: songForm.value.notes.trim() || null,
          isDisabled: songForm.value.isDisabled
        };

        const url = editingSong.value ? `/api/songs/${editingSong.value.id}` : "/api/songs";

        const method = editingSong.value ? "put" : "post";

        const response = await api[method](url, songData);

        if (response.data.success) {
          toast.add({
            severity: "success",
            summary: "Success",
            detail: editingSong.value ? "Song updated successfully" : "Song created successfully",
            life: 3000
          });

          await fetchSongs();
          cancelSongModal();
        } else {
          songError.value = response.data.message || "Failed to save song";
        }
      } catch (err) {
        console.error("Save song error:", err);
        songError.value =
          err.response?.data?.message ||
          err.response?.data?.errors?.join(", ") ||
          "Failed to save song";
      } finally {
        songLoading.value = false;
      }
    };

    onMounted(() => {
      fetchSongs();
      fetchCategories();
      fetchGenres();
    });

    return {
      songs,
      loading,
      categories,
      genres,
      showSongModal,
      editingSong,
      songForm,
      songError,
      songLoading,
      togglingSongId,
      openAddModal,
      openEditModal,
      cancelSongModal,
      handleOutsideClick,
      handleSaveSong,
      toggleSongStatus,
      handleDeleteSong,
      cancelDelete,
      confirmDelete,
      showDeleteModal,
      songToDelete,
      deletingSongId,
      rows,
      songSearch,
      statusFilter,
      genreFilter,
      categoryFilter,
      songFilterClass,
      songLabelClass,
      songFieldClass,
      showCategoryMenu,
      categoryChoices,
      genreChoices,
      setSongLengthPart,
      closeCategoryMenu,
      songCategoriesLabel,
      filteredSongs
    };
  }
};
</script>

<style scoped>
/* Style ToggleSwitch OFF state (when song is disabled) to be red */
:deep(.song-toggle-disabled:not(.p-toggleswitch-checked) .p-toggleswitch-slider) {
  background-color: #ef4444 !important; /* red-500 */
}

/* Keep the toggle handle white */
:deep(.song-toggle-disabled .p-toggleswitch-slider:before) {
  background-color: white;
}
</style>

<style>
.dark .p-datatable table {
  background-color: #111827;
  color: #e5e7eb;
}

.dark .p-datatable .p-datatable-thead > tr > th {
  background-color: #1f2937;
  color: #e5e7eb;
  border-color: #374151;
}

.dark .p-datatable .p-datatable-tbody > tr > td {
  background-color: #111827;
  color: #e5e7eb;
  border-color: #1f2937;
}

.dark .p-datatable .p-datatable-tbody > tr.p-highlight > td {
  background-color: #1f2937;
}

.dark .p-datatable .p-datatable-paginator-bottom {
  background-color: #0f172a;
  color: #e5e7eb;
  border-top: 1px solid #1f2937;
}

.dark .p-paginator .p-paginator-page,
.dark .p-paginator .p-paginator-prev,
.dark .p-paginator .p-paginator-next,
.dark .p-paginator .p-paginator-first,
.dark .p-paginator .p-paginator-last {
  background-color: #0f172a;
  color: #e5e7eb;
  border-color: #1f2937;
}

.dark .p-paginator .p-paginator-page.p-highlight {
  background-color: #1f2937;
  color: #d1d5db;
  border-color: #334155;
}
</style>
